import { useEffect, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover'
import { getAdminOrganizations } from '@/services/api'

interface OrganizationComboboxProps {
  /** Organization id, or null for "all organizations". */
  value: string | null
  /** Display name for the current value — the caller already has it (from the
   *  org list or an org-detail fetch), so this doesn't need its own lookup. */
  selectedLabel: string
  onChange: (organizationId: string | null) => void
  className?: string
}

/**
 * Organization picker with server-side search-as-you-type, so it works
 * correctly regardless of how many organizations exist — unlike a plain
 * <Select> populated from one unfiltered page, which silently only offers
 * whatever fit on that page.
 */
export function OrganizationCombobox({ value, selectedLabel, onChange, className }: OrganizationComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 250)
    return () => clearTimeout(timer)
  }, [search])

  const { data, isFetching } = useQuery({
    queryKey: ['admin-organizations-typeahead', debouncedSearch],
    queryFn: () => getAdminOrganizations({ q: debouncedSearch || undefined, page: 1 }),
    enabled: open,
    placeholderData: keepPreviousData,
  })

  const options = data?.data ?? []

  function select(organizationId: string | null) {
    onChange(organizationId)
    setOpen(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          setSearch('')
          setDebouncedSearch('')
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center justify-between gap-1.5 rounded-md border border-input bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 focus:border-primary focus:outline-none',
            className,
          )}
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search organizations…"
          className="mb-1.5 w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm placeholder:text-gray-400 focus:border-primary focus:outline-none"
          autoFocus
        />
        <div className="max-h-60 overflow-y-auto">
          <button
            type="button"
            onClick={() => select(null)}
            className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
          >
            <span className="flex h-4 w-4 shrink-0 items-center justify-center text-primary">
              {value === null && <Check className="h-3.5 w-3.5" />}
            </span>
            All organizations
          </button>

          {options.length === 0 ? (
            <p className="px-2 py-3 text-center text-xs text-gray-400">
              {isFetching ? 'Searching…' : 'No results'}
            </p>
          ) : (
            options.map((org) => (
              <button
                key={org.id}
                type="button"
                onClick={() => select(org.id)}
                className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center text-primary">
                  {value === org.id && <Check className="h-3.5 w-3.5" />}
                </span>
                <span className="truncate">{org.name}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
